import {
  DragDropContext,
  Droppable,
  Draggable,
  OnDragEndResponder,
} from 'react-beautiful-dnd';

type DragAndDropWithoutContextProps<T> = {
  droppableId: string;
  isDropDisabled?: boolean;
  isDragDisabled?: boolean;
  draggableItems: T[];
  itemRenderer: (
    item: T,
    isDragging: boolean,
    dragHandleProps: any
  ) => React.ReactNode;
  getDroppableStyle?: (isDraggingOver: boolean) => any;
  getDraggableStyle?: (isDragging: boolean, draggableStyle: any) => any;
  getDraggableId: (item: T) => string;
};

type DragAndDropProps<T> = {
  onDragEnd: OnDragEndResponder;
} & DragAndDropWithoutContextProps<T>;

export function DragAndDropWithoutContext<T>({
  droppableId,
  isDropDisabled,
  isDragDisabled,
  draggableItems,
  itemRenderer,
  getDroppableStyle,
  getDraggableStyle,
  getDraggableId,
}: Readonly<DragAndDropWithoutContextProps<T>>) {
  return (
    <Droppable droppableId={droppableId} isDropDisabled={isDropDisabled}>
      {(provided: any, droppableSnapshot: any) => (
        <div
          ref={provided.innerRef}
          style={
            getDroppableStyle
              ? getDroppableStyle(droppableSnapshot.isDraggingOver)
              : undefined
          }
          {...provided.droppableProps}
        >
          {draggableItems.map((item, index) => (
            <Draggable
              key={getDraggableId(item)}
              draggableId={getDraggableId(item)}
              index={index}
              isDragDisabled={isDragDisabled}
            >
              {(provided: any, draggableSnapshot: any) => (
                <div
                  ref={provided.innerRef}
                  style={
                    getDraggableStyle
                      ? getDraggableStyle(
                          draggableSnapshot.isDragging,
                          provided.draggableProps.style
                        )
                      : undefined
                  }
                  {...provided.dragHandleProps}
                  {...provided.draggableProps}
                >
                  {itemRenderer(
                    item,
                    draggableSnapshot.isDragging,
                    provided.dragHandleProps
                  )}
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}

export default function DragAndDrop<T>({
  onDragEnd,
  ...rest
}: Readonly<DragAndDropProps<T>>) {
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <DragAndDropWithoutContext<T> {...rest} />
    </DragDropContext>
  );
}
